import furLogo from "../../images/furLogo.png";
import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import { Bio } from '../../data/constants';

const FooterContainer = styled.footer`
  width: 100%;
  padding: 80px 24px 32px;
  background: rgba(8, 9, 17, 0.95);
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(16px);
`;

const FooterWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const FooterTop = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 32px;
`;

const Brand = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  color: ${({ theme }) => theme.text_primary};
`;

const LogoImage = styled.img`
  width: 140px;
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 8px 18px rgba(0,0,0,0.35));
`;

const Tagline = styled.p`
  color: ${({ theme }) => theme.text_secondary};
  line-height: 1.7;
  margin: 0;
`;

const LinksColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ColumnTitle = styled.h4`
  margin: 0;
  font-size: 16px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.text_secondary};
`;

const LinksGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const NavLinkStyled = styled(Link)`
  color: ${({ theme }) => theme.text_primary};
  text-decoration: none;
  font-weight: 500;
  &:hover {
    color: ${({ theme }) => theme.primary};
  }
`;

const ContactItem = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  color: ${({ theme }) => theme.text_primary};
  text-decoration: none;
  font-weight: 500;
  svg {
    color: ${({ theme }) => theme.primary};
  }
`;

const SocialRow = styled.div`
  display: flex;
  gap: 12px;
`;

const SocialButton = styled.a`
  width: 42px;
  height: 42px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.text_primary};
  text-decoration: none;
  transition: border-color 0.2s ease, color 0.2s ease;
  &:hover {
    border-color: ${({ theme }) => theme.primary};
    color: ${({ theme }) => theme.primary};
  }
`;

const FooterBottom = styled.div`
  padding-top: 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const Legal = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.text_secondary};
  font-size: 14px;
`;

function Footer() {
  const footerLinks = [
    { label: 'Home', to: '/' },
    { label: 'Experience', to: '/experience' },
    { label: 'Projects', to: '/projects' },
    { label: 'Education', to: '/education' },
    { label: 'Contact', to: '/contact' },
  ];

  return (
    <FooterContainer>
      <FooterWrapper>
        <FooterTop>
          <Brand>
            <LogoImage src={furLogo} alt="Mohammed Furquan Saleem Logo" />
            <Tagline>
              AI/ML engineer crafting intelligent experiences for data-driven
              products, from concept research to production launch.
            </Tagline>
            <SocialRow>
              <SocialButton href={Bio.linkedin} target="_blank" rel="noreferrer">
                <LinkedInIcon />
              </SocialButton>
              <SocialButton href={Bio.github} target="_blank" rel="noreferrer">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 .5C5.65.5.5 5.65.5 12a11.5 11.5 0 008.06 10.94c.59.11.8-.26.8-.58v-2.08c-3.28.71-3.97-1.59-3.97-1.59a3.15 3.15 0 00-1.33-1.75c-1.09-.75.08-.74.08-.74a2.48 2.48 0 011.81 1.22 2.52 2.52 0 003.44 1 2.51 2.51 0 01.75-1.58c-2.63-.3-5.39-1.32-5.39-5.89a4.6 4.6 0 011.22-3.19 4.29 4.29 0 01.12-3.15s1-.32 3.3 1.21a11.32 11.32 0 016 0c2.3-1.53 3.3-1.21 3.3-1.21a4.29 4.29 0 01.12 3.15 4.6 4.6 0 011.22 3.19c0 4.59-2.76 5.58-5.4 5.88a2.82 2.82 0 01.79 2.2v3.26c0 .32.21.7.81.58A11.5 11.5 0 0023.5 12C23.5 5.65 18.35.5 12 .5z" />
                </svg>
              </SocialButton>
              <SocialButton href={Bio.twitter} target="_blank" rel="noreferrer">
                <TwitterIcon />
              </SocialButton>
              <SocialButton href={Bio.insta} target="_blank" rel="noreferrer">
                <InstagramIcon />
              </SocialButton>
            </SocialRow>
          </Brand>
          <LinksColumn>
            <ColumnTitle>Navigate</ColumnTitle>
            <LinksGrid>
              {footerLinks.map(({ label, to }) => (
                <NavLinkStyled key={label} to={to}>
                  {label}
                </NavLinkStyled>
              ))}
            </LinksGrid>
          </LinksColumn>
          <LinksColumn>
            <ColumnTitle>Contact</ColumnTitle>
            <LinksGrid>
              <ContactItem href="mailto:mfurquan.sal@gmail.com">
                <EmailRoundedIcon />
                mfurquan.sal@gmail.com
              </ContactItem>
              <ContactItem href="tel:+919951598025">
                <PhoneIphoneIcon />
                +91 995 159 8025
              </ContactItem>
            </LinksGrid>
          </LinksColumn>
        </FooterTop>
        <FooterBottom>
          <Legal>&copy; {new Date().getFullYear()} Mohammed Furquan Saleem. All Rights Reserved.</Legal>
          </FooterBottom>
        </FooterWrapper>
    </FooterContainer>
  );
}

export default Footer;